// One frame for every app screen (Nav, 2026-10-04: pages felt like different
// websites). The header and footer match the marketing site's, the content
// column is the same width everywhere, and cross-page view transitions keep
// the header still. The vote page and the big screen stay full focus.
import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { formatCode, parseCode } from "@flp/core";
import { navigate } from "../router";
import { Hash, LayoutTemplate, LifeBuoy, ListChecks, Menu, Plus, Tag, X } from "lucide-preact";
import { locale } from "../i18n";
import { t } from "../i18n";
import { Logo } from "./Chrome";
import { lazy, Suspense } from "preact/compat";
// Loaded with the footer, not the app: the vote page has no footer and a 50 KB budget.
const LanguagePicker = lazy(() => import("./LanguagePicker").then((m) => ({ default: m.LanguagePicker })));
import { ThemeSwitch } from "./ThemeSwitch";

/** A marketing-site page in the reader's language (the site prefixes every language but English). */
/** The template library exists in English and Spanish; every other language opens the English one. */
function templatesPath(): string {
  return locale().toLowerCase() === "es" ? "/es/polls" : "/polls";
}

function sitePath(slug: string): string {
  const l = locale().toLowerCase();
  return l === "en" ? `/${slug}` : `/${l}${slug ? `/${slug}` : ""}`;
}

export function Shell({ children, here }: { children: ComponentChildren; here?: string }) {
  // The header turns into a floating capsule once the page scrolls (base.css, .appbar-in).
  useEffect(() => {
    const set = () => document.documentElement.classList.toggle("scrolled", scrollY > 8);
    set();
    addEventListener("scroll", set, { passive: true });
    return () => removeEventListener("scroll", set);
  }, []);
  return (
    <div class="shell">
      <header class="appbar">
        <div class="appbar-in">
          <Logo href={sitePath("")} />
          <nav class="appbar-end" aria-label="Main">
            <a class="appbar-link" href={templatesPath()}>{t("nav.templates")}</a>
            <a class="appbar-link" href={sitePath("pricing")}>{t("nav.pricing")}</a>
            <a class="appbar-link" href={sitePath("help")}>{t("nav.help")}</a>
            <a class="appbar-link" href="/my" aria-current={here === "my" ? "page" : undefined}>{t("nav.my_polls")}</a>
            <JoinButton />
            {/* My polls has its own New poll button under the title; two on one phone screen is clutter (visual scan, 2026-10-29). */}
            {here !== "create" && here !== "my" && (
              <a class="btn primary small nav-cta" href="/new">
                <Plus size={16} aria-hidden="true" />
                <span class="cta-long">{t("nav.create_poll")}</span>
                <span class="cta-short">{t("nav.create_poll_short")}</span>
              </a>
            )}
            <MobileMenu />
          </nav>
        </div>
      </header>
      <div class="shell-body">{children}</div>
      <footer class="appfoot">
        <div class="appfoot-in">
          {/* The header carries the full logo; the footer only the mark. */}
          <a class="foot-mark" href={sitePath("")} aria-label="LivePoll">
            <img src="/assets/brand/icon.svg" alt="" width={22} height={24} />
            <span>LivePoll</span>
          </a>
          <nav aria-label={t("nav.help")}>
            <a href={templatesPath()}>{t("nav.templates")}</a>
            <a href={sitePath("pricing")}>{t("nav.pricing")}</a>
            <a href={sitePath("help")}>{t("nav.help")}</a>
            <a href="/my">{t("nav.my_polls")}</a>
            <a href={sitePath("legal")}>{t("nav.legal")}</a>
          </nav>
          <div class="foot-end">
            <ThemeSwitch />
            <Suspense fallback={<span class="lang-slot" />}>
              <LanguagePicker />
            </Suspense>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** "Join a poll": a popover under the button (a bottom sheet on phones) with the code field focused. */
function JoinButton() {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const wrap = useRef<HTMLSpanElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const code = parseCode(value);
  const slug = value.trim().toLowerCase();
  const target = code ? `/${formatCode(code)}` : /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) && slug.length >= 3 && /[a-z]/.test(slug) ? `/${slug}` : null;
  useEffect(() => {
    if (!open) return;
    document.documentElement.classList.add("join-open");
    input.current?.focus();
    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const down = (e: PointerEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    addEventListener("keydown", key);
    addEventListener("pointerdown", down);
    return () => {
      document.documentElement.classList.remove("join-open");
      removeEventListener("keydown", key);
      removeEventListener("pointerdown", down);
    };
  }, [open]);
  return (
    <span class="join-wrap" ref={wrap}>
      <a
        class="nav-join"
        href="/join"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t("nav.join_poll")}
        data-tip={t("nav.join_poll")}
        onClick={(e) => {
          e.preventDefault();
          setValue("");
          setOpen(!open);
        }}
      >
        <Hash size={16} aria-hidden="true" />
        <span class="join-label">{t("nav.join_poll")}</span>
      </a>
      {open && (
        <form
          class="join-pop"
          role="dialog"
          aria-label={t("nav.join_poll")}
          onSubmit={(e) => {
            e.preventDefault();
            if (!target) return;
            setOpen(false);
            if (code) navigate(target);
            else location.assign(target);
          }}
        >
          <label class="join-pop-title" for="join-pop-code">
            {t("join.title")}
          </label>
          <div class="join-pop-row">
            <input
              id="join-pop-code"
              ref={input}
              class="join-pop-input"
              inputMode="numeric"
              autoComplete="off"
              maxLength={40}
              placeholder={t("join.placeholder")}
              value={value}
              onInput={(e) => setValue((e.target as HTMLInputElement).value)}
            />
            <button class="btn primary" type="submit" disabled={!target}>
              {t("join.go")}
            </button>
          </div>
        </form>
      )}
    </span>
  );
}

/** Below 1000px the header links move into a full-screen menu behind a hamburger. */
function MobileMenu() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    document.documentElement.classList.add("mnav-open");
    const key = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    const size = () => innerWidth > 1000 && setOpen(false);
    addEventListener("keydown", key);
    addEventListener("resize", size);
    return () => {
      document.documentElement.classList.remove("mnav-open");
      removeEventListener("keydown", key);
      removeEventListener("resize", size);
    };
  }, [open]);
  return (
    <>
      <button class="nav-burger" type="button" aria-label={t("nav.menu")} aria-expanded={open} onClick={() => setOpen(true)}>
        <Menu size={22} aria-hidden="true" />
      </button>
      {open && (
        <div class="mnav" role="dialog" aria-modal="true" aria-label={t("nav.menu")}>
          <div class="mnav-top">
            <Logo href={sitePath("")} />
            <button class="nav-burger close" type="button" aria-label={t("nav.close_menu")} onClick={() => setOpen(false)} ref={(el) => el?.focus()}>
              <X size={22} aria-hidden="true" />
            </button>
          </div>
          <nav class="mnav-links" aria-label={t("nav.menu")}>
            <a href={templatesPath()}><LayoutTemplate size={20} aria-hidden="true" />{t("nav.templates")}</a>
            <a href={sitePath("pricing")}><Tag size={20} aria-hidden="true" />{t("nav.pricing")}</a>
            <a href={sitePath("help")}><LifeBuoy size={20} aria-hidden="true" />{t("nav.help")}</a>
            <a href="/my" onClick={() => setOpen(false)}><ListChecks size={20} aria-hidden="true" />{t("nav.my_polls")}</a>
            <a href="/join" onClick={() => setOpen(false)}><Hash size={20} aria-hidden="true" />{t("nav.join_poll")}</a>
          </nav>
          <a class="btn primary mnav-cta" href="/new" onClick={() => setOpen(false)}>{t("nav.create_poll")}</a>
          <div class="mnav-theme">
            <ThemeSwitch />
          </div>
        </div>
      )}
    </>
  );
}
